import {
  Component,
  input,
  output,
  forwardRef,
  computed,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  inject,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';


@Component({
  selector: 'sb-text-area',
  standalone: true,
  imports: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="w-full flex flex-col gap-y-1">
      @if (label() && !hideLabel()) {
      <label
        [for]="id()"
        class="block text-sm text-gray-700 transition-colors duration-200"
        [class.sr-only]="hideLabel()"
      >
        {{ label() }}
        @if (required()) {
        <span class="text-red-500">*</span>
        }
      </label>
      }

      <textarea
        [id]="id()"
        [name]="name() || id()"
        [autocomplete]="autocomplete()"
        [required]="required()"
        [disabled]="disabled()"
        [readonly]="readonly()"
        [placeholder]="placeholder()"
        [value]="value"
       
        [rows]="rows()"
        [cols]="cols()"
        [maxLength]="maxLength()"
        [minLength]="minLength()"
        (input)="onInput($event)"
        (blur)="onBlur()"
        (focus)="onFocus()"
        [class]="textareaClasses()"
      ></textarea>

      @if (errorMessage()) {
      <p
        class="text-xs text-red-600 animate-slideInLeft"
        style="animation-delay: 50ms;"
      >
        {{ errorMessage() }}
      </p>
      } @if (helpText() && !errorMessage()) {
      <p class="text-xs text-gray-400">
        {{ helpText() }}
      </p>
      }
    </div>
  `,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TextAreaComponent),
      multi: true,
    },
  ],
})
export class TextAreaComponent implements ControlValueAccessor {
  private cdr = inject(ChangeDetectorRef);

  // Input signals
  id = input.required<string>();
  name = input<string>();
  label = input<string>();
  placeholder = input<string>('');
  autocomplete = input<string>();
  required = input<boolean>(false);
  readonly = input<boolean>(false);
  disabled = input<boolean>(false);
  hideLabel = input<boolean>(false);
  errorMessage = input<string | null | undefined>(null);
  helpText = input<string>();
  variant = input<'default' | 'stacked'>('default');
  size = input<'sm' | 'md' | 'lg'>('md');
  autofocus = input<boolean>(false);
  rows = input<number>(3);
  cols = input<number>();
  maxLength = input<number>();
  minLength = input<number>();
  resize = input<'none' | 'both' | 'horizontal' | 'vertical'>('vertical');

  // Output signals
  inputChange = output<string>();
  inputBlur = output<void>();
  inputFocus = output<void>();

  value = '';

  private onChange = (value: string) => {};
  private onTouched = () => {};

  textareaClasses = computed(() => {
    const baseClasses = [
      'appearance-none relative block w-full px-3 py-2 border rounded-md',
      'placeholder-gray-300 text-gray-900 bg-white',
      'focus:outline-none focus:ring-orange-500 focus:border-orange-500 focus:z-10',
      'transition-all duration-200 ease-in-out',
    ];

    // Size classes
    const sizeClasses = {
      sm: 'text-xs py-1.5',
      md: 'text-sm py-2',
      lg: 'text-base py-3',
    };

    // Variant classes
    const variantClasses = {
      default: 'rounded-md',
      stacked: 'rounded-none first:rounded-t-md last:rounded-b-md',
    };

    // State classes
    const stateClasses = this.errorMessage()
      ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
      : 'border-gray-300';

    const disabledClasses = this.disabled()
      ? 'bg-gray-100 text-gray-400 cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-500'
      : this.readonly()
      ? 'bg-gray-50 cursor-default'
      : '';

    // Resize classes
    const resizeClasses = {
      none: 'resize-none',
      both: 'resize',
      horizontal: 'resize-x',
      vertical: 'resize-y',
    };

    return [
      ...baseClasses,
      sizeClasses[this.size()],
      variantClasses[this.variant()],
      stateClasses,
      disabledClasses,
      resizeClasses[this.resize()],
    ]
      .filter(Boolean)
      .join(' ');
  });

  onInput(event: Event): void {
    const target = event.target as HTMLTextAreaElement;
    this.value = target.value;
    this.onChange(this.value);
    this.inputChange.emit(this.value);
  }

  onBlur(): void {
    this.onTouched();
    this.inputBlur.emit();
  }

  onFocus(): void {
    this.inputFocus.emit();
  }

  // ControlValueAccessor implementation
  writeValue(value: string): void {
    this.value = value || '';
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    // Note: We can't directly set the disabled signal from here
    // The parent component should handle this through the disabled input
    // For now, we'll handle this in the component logic
  }
}
